import { normalizeForDiff } from '@/lib/v3/text-compare';

/**
 * Lines longer than this are treated as run-on text (several lyric lines
 * mashed into one, e.g. raw Whisper output or a pasted paragraph) and get
 * deterministically split before counting/pricing.
 */
const RUNON_LINE_CHAR_LIMIT = 150;
/**
 * Hard cap on words per line when splitting run-on text. Splits always
 * happen between words, never in the middle of a word.
 */
const RUNON_WORD_CAP = 15;
/**
 * Hard submission cap: a lyric "line" with more than this many words is
 * several lines mashed into one. Pricing is per line, so an order cannot be
 * submitted while any line exceeds this — the customer must split it with
 * line breaks first. Transcription output never trips it (run-on splitting
 * caps lines at RUNON_WORD_CAP); only pasted walls do.
 */
export const MAX_WORDS_PER_LINE = 20;

// Abbreviations whose trailing period is NOT a sentence end when splitting
// run-on text ("Mr. Sandman" must not become "Mr." on its own line).
const ABBREVIATION_RE = /^(mr|mrs|ms|dr|st|jr|sr|prof|vs|etc)$/i;

/**
 * Split one run-on text chunk at sentence terminators (. ! ? …), keeping the
 * punctuation attached to the sentence before it. Abbreviations and initials
 * ("Mr.", "U.S.A.", "J.") are not treated as sentence ends.
 */
function splitIntoSentences(line: string): string[] {
  const parts: string[] = [];
  let start = 0;
  const re = /([.!?…]+)(\s+|$)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(line)) !== null) {
    const textBefore = line.slice(start, match.index);
    const lastWord = textBefore.trim().split(/\s+/).pop() ?? '';
    const isAbbreviation =
      ABBREVIATION_RE.test(lastWord) ||
      lastWord.includes('.') || // "U.S.A"
      /^[A-Za-z]$/.test(lastWord); // single initial
    if (isAbbreviation) continue;
    parts.push(line.slice(start, match.index + match[1].length).trim());
    start = re.lastIndex;
  }
  if (line.slice(start).trim()) parts.push(line.slice(start).trim());
  return parts;
}

/**
 * Split a chunk at commas/semicolons, keeping the separator attached to the
 * piece before it. Used for sentences that still exceed the word cap.
 */
function splitOnSeparators(chunk: string): string[] {
  const tokens = chunk.split(/([,;])\s*/);
  const parts: string[] = [];
  let current = '';
  tokens.forEach(token => {
    current += token;
    if (token === ',' || token === ';') {
      if (current.trim()) parts.push(current.trim());
      current = '';
    }
  });
  if (current.trim()) parts.push(current.trim());
  return parts;
}

/**
 * Hard-cap a piece at RUNON_WORD_CAP words. Splits only at word boundaries —
 * a word is never cut in half. No-op for pieces already within the cap.
 */
function capWords(piece: string): string[] {
  const words = piece.trim().split(/\s+/);
  if (words.length <= RUNON_WORD_CAP) return [piece.trim()];
  const out: string[] = [];
  for (let i = 0; i < words.length; i += RUNON_WORD_CAP) {
    out.push(words.slice(i, i + RUNON_WORD_CAP).join(' '));
  }
  return out;
}

/**
 * Deterministically normalize lyrics that arrived as run-on walls of text.
 * Lines at or under RUNON_LINE_CHAR_LIMIT pass through untouched; longer
 * lines are split at sentence boundaries, then commas, then a hard word cap.
 * This is what keeps pricing honest whether the text came from a failed
 * transcription format pass or a customer pasting a paragraph to dodge the
 * per-line price.
 */
export function splitRunOnLyrics(text: string): string {
  const normalized = formatLyrics(text);
  if (!normalized) return normalized;
  const outputLines: string[] = [];
  normalized.split('\n').forEach(rawLine => {
    const line = rawLine.trim();
    if (line.length <= RUNON_LINE_CHAR_LIMIT) {
      outputLines.push(rawLine);
      return;
    }
    splitIntoSentences(line).forEach(sentence => {
      const pieces =
        sentence.trim().split(/\s+/).length > RUNON_WORD_CAP
          ? splitOnSeparators(sentence)
          : [sentence];
      pieces.forEach(piece => capWords(piece).forEach(capped => outputLines.push(capped)));
    });
  });
  return outputLines.join('\n');
}

/**
 * Find the first lyric line that exceeds maxWords (blank lines skipped).
 * Returns the 1-based line number as the customer sees it in the textarea
 * plus its word count, or null when every line is within the cap. Used to
 * block order submission until run-on lines are split.
 */
export function findOverlongLyricLine(
  text: string,
  maxWords: number = MAX_WORDS_PER_LINE
): { lineNumber: number; words: number } | null {
  const lines = formatLyrics(text).split('\n');
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const words = lines[i].trim().split(/\s+/).length;
    if (words > maxWords) return { lineNumber: i + 1, words };
  }
  return null;
}

/**
 * Count how many segments (lines) differ between original and modified lyrics.
 * Extra or missing lines count as changes. Run-on lines are normalized first
 * (splitRunOnLyrics) so a wall of text cannot be counted as one line.
 *
 * Equal line counts keep the launch-day pairwise index comparison, byte for
 * byte. Unequal counts (a customer intentionally adding/removing lines) are
 * aligned first — with naive index pairing a single mid-wall insertion shifts
 * every later line onto the wrong partner and bills the whole back half of
 * the song. Paired-but-different, missing, and extra lines each count as one
 * changed segment, mirroring the equal-count semantics.
 */
export function countChangedSegments(original: string, modified: string): number {
  const originalLines = splitRunOnLyrics(original)
    .split('\n')
    .filter(line => line.trim() !== '');
  const modifiedLines = splitRunOnLyrics(modified)
    .split('\n')
    .filter(line => line.trim() !== '');

  if (originalLines.length === modifiedLines.length) {
    let changed = 0;
    for (let i = 0; i < originalLines.length; i++) {
      const origLine = originalLines[i].trim().toLowerCase();
      const modLine = modifiedLines[i].trim().toLowerCase();
      if (origLine !== modLine) {
        changed++;
      }
    }
    return changed;
  }

  let changed = 0;
  for (const ev of alignmentEvents(originalLines, modifiedLines)) {
    if (ev.kind === 'pair') {
      const origLine = originalLines[ev.origIdx].trim().toLowerCase();
      const modLine = modifiedLines[ev.modIdx].trim().toLowerCase();
      if (origLine !== modLine) changed++;
    } else {
      changed++;
    }
  }
  return changed;
}

/** One step of the line-level alignment between two walls. */
type AlignEvent =
  | { kind: 'pair'; origIdx: number; modIdx: number }
  | { kind: 'missing'; origIdx: number }
  | { kind: 'extra'; modIdx: number };

/**
 * Align two arrays of non-blank lyric lines into ordered events: 'pair' when
 * the lines correspond (equal after normalizeForDiff, or a rewrite in the
 * same slot), 'missing' when an original line has no right-side counterpart,
 * 'extra' when a right-side line has no original counterpart. Common prefix
 * and suffix anchor the walls; inside the remaining block a short lookahead
 * resyncs past runs of missing/extra lines, and everything else pairs
 * in-place as a rewrite. Shared by the mismatch diagnosis and the auto-fix.
 */
function alignmentEvents(origC: string[], modC: string[]): AlignEvent[] {
  const eq = (a: string, b: string) => normalizeForDiff(a) === normalizeForDiff(b);
  const LOOKAHEAD = 3;
  const resync = (arr: string[], start: number, end: number, target: string): number => {
    for (let k = start + 1; k < Math.min(end, start + 1 + LOOKAHEAD); k++) {
      if (eq(arr[k], target)) return k - start;
    }
    return 0;
  };

  const shorter = Math.min(origC.length, modC.length);
  let prefix = 0;
  while (prefix < shorter && eq(origC[prefix], modC[prefix])) prefix++;
  let suffix = 0;
  while (
    suffix < shorter - prefix &&
    eq(origC[origC.length - 1 - suffix], modC[modC.length - 1 - suffix])
  ) {
    suffix++;
  }
  const origEnd = origC.length - suffix;
  const modEnd = modC.length - suffix;

  const events: AlignEvent[] = [];
  for (let k = 0; k < prefix; k++) events.push({ kind: 'pair', origIdx: k, modIdx: k });

  let i = prefix;
  let j = prefix;
  while (i < origEnd && j < modEnd) {
    if (eq(origC[i], modC[j])) {
      events.push({ kind: 'pair', origIdx: i, modIdx: j });
      i++;
      j++;
      continue;
    }
    const skipOrig = resync(origC, i, origEnd, modC[j]);
    const skipMod = resync(modC, j, modEnd, origC[i]);
    if (skipOrig > 0 && (skipMod === 0 || skipOrig <= skipMod)) {
      for (let k = 0; k < skipOrig; k++) events.push({ kind: 'missing', origIdx: i + k });
      i += skipOrig;
    } else if (skipMod > 0) {
      for (let k = 0; k < skipMod; k++) events.push({ kind: 'extra', modIdx: j + k });
      j += skipMod;
    } else {
      // Same slot, different text: the customer rewrote this line.
      events.push({ kind: 'pair', origIdx: i, modIdx: j });
      i++;
      j++;
    }
  }
  while (i < origEnd) {
    events.push({ kind: 'missing', origIdx: i });
    i++;
  }
  while (j < modEnd) {
    events.push({ kind: 'extra', modIdx: j });
    j++;
  }
  for (let k = 0; k < suffix; k++) {
    events.push({ kind: 'pair', origIdx: origEnd + k, modIdx: modEnd + k });
  }
  return events;
}

/**
 * One structural break between the walls: a run of consecutive lines on one
 * side with no counterpart on the other. 'missing' lines live in the
 * original (the right side is short there); 'extra' lines live in the
 * modified wall.
 */
export interface LineBreak {
  kind: 'missing' | 'extra';
  /** 1-based line number of the first line in the run, in the wall it lives in. */
  lineNumber: number;
  count: number;
  /** Text of the run's first line. */
  text: string;
}

export interface LineDivergence {
  origCount: number;
  modCount: number;
  /** Structural breaks in wall order; always non-empty when counts differ. */
  breaks: LineBreak[];
}

/**
 * Locate the structural breaks between two walls whose non-blank line counts
 * differ, for the order form's line-count mismatch diagnosis. Lines are
 * matched via normalizeForDiff (case/punctuation tweaks are "matching"), so
 * genuine content edits do not hide the spot where a line is missing or
 * extra. Returns null when the counts are equal or either side is blank.
 */
export function describeLineDivergence(original: string, modified: string): LineDivergence | null {
  const origC = original.split('\n').filter(l => l.trim());
  const modC = modified.split('\n').filter(l => l.trim());
  if (origC.length === 0 || modC.length === 0 || origC.length === modC.length) return null;

  const breaks: LineBreak[] = [];
  for (const ev of alignmentEvents(origC, modC)) {
    if (ev.kind === 'pair') continue;
    const lineNumber = (ev.kind === 'missing' ? ev.origIdx : ev.modIdx) + 1;
    const last = breaks[breaks.length - 1];
    if (last && last.kind === ev.kind && last.lineNumber + last.count === lineNumber) {
      last.count++;
    } else {
      breaks.push({
        kind: ev.kind,
        lineNumber,
        count: 1,
        text: ev.kind === 'missing' ? origC[ev.origIdx] : modC[ev.modIdx],
      });
    }
  }
  return { origCount: origC.length, modCount: modC.length, breaks };
}

/**
 * Producer-facing summary of a customer-confirmed structural edit (unequal
 * line counts). Built identically on the client (step-3 callout, so the
 * customer sees exactly what the producer will see) and in create-checkout
 * (appended to notes_to_producer). Null when the walls already line up.
 */
export function buildStructuralChangeNote(original: string, modified: string): string | null {
  const origFull = formatLyrics(original);
  const modFull = formatLyrics(modified);
  const origCount = origFull.split('\n').filter(l => l.trim()).length;
  const modCount = modFull.split('\n').filter(l => l.trim()).length;
  if (!origCount || !modCount || origCount === modCount) return null;

  const shorten = (line: string, max = 50) => {
    const t = line.trim();
    return t.length > max ? `${t.slice(0, max - 3)}...` : t;
  };

  const lines = [
    `[STRUCTURAL EDIT: customer confirmed the line-count difference is intentional (original ${origCount} lines, modified ${modCount} lines). Produce against the modified wall as submitted.]`,
  ];
  const div = describeLineDivergence(origFull, modFull);
  for (const b of div.breaks.slice(0, 4)) {
    if (b.kind === 'missing') {
      lines.push(
        `- Original line ${b.lineNumber}${b.count > 1 ? ` through ${b.lineNumber + b.count - 1}` : ''} has no modified counterpart (customer removed): "${shorten(b.text)}".`,
      );
    } else {
      lines.push(
        `- Modified line ${b.lineNumber}${b.count > 1 ? ` through ${b.lineNumber + b.count - 1}` : ''} has no original counterpart (customer added): "${shorten(b.text)}".`,
      );
    }
  }
  if (div.breaks.length > 4) {
    lines.push(`- Plus ${div.breaks.length - 4} more changed spot${div.breaks.length - 4 === 1 ? '' : 's'}; see the two lyric walls.`);
  }
  return lines.join('\n');
}

/**
 * Detect content lines that repeat as an exact whole-number multiple (the
 * pasted-twice accident). Returns the first copy's lines, or null. Mirrors
 * Overdub Studio's detectDoubledLines, inlined here so the service helpers
 * stay dependency-free for the server bundle. ABAB stanzas and repeated
 * hooks never match: only whole-wall repetition does.
 */
function detectWholeWallRepetition(lines: string[]): string[] | null {
  const n = lines.length;
  for (const factor of [2, 3]) {
    if (n < factor * 2 || n % factor !== 0) continue;
    const unit = n / factor;
    let all = true;
    for (let i = unit; i < n; i++) {
      if (normalizeForDiff(lines[i]) !== normalizeForDiff(lines[i - unit])) {
        all = false;
        break;
      }
    }
    if (all) return lines.slice(0, unit);
  }
  return null;
}

/**
 * A modified line this short, starting lowercase, right after a line that
 * does not end a sentence, is usually the second half of one sung line the
 * customer's paste broke in two ("...while I've got time tac" / "taco").
 */
const SPLIT_FRAGMENT_MAX_WORDS = 3;

export interface AutoAlignResult {
  /** Repaired modified wall: same non-blank line count as the original. */
  fixed: string;
  /** Plain-language actions the fixer took, shown to the customer. */
  summary: string[];
  changed: boolean;
}

/**
 * Deterministically repair a mismatched modified wall so it lines up 1:1
 * with the original (same non-blank line count, same stanza breaks).
 *
 * Policy: missing right-side lines are filled in from the original (they
 * were unchanged lines that got lost in pasting); duplicated right-side
 * lines are dropped; a genuinely extra right-side line is merged into the
 * line above when the merged line stays within MAX_WORDS_PER_LINE, and only
 * dropped (and said so) when even that is impossible. The customer's
 * rewritten lines are never touched. Returns null when there is nothing to
 * fix (counts already match or either side is blank) or when the rebuild
 * cannot produce a valid wall (never happens in practice; defensive).
 */
export function autoAlignLyrics(original: string, modified: string): AutoAlignResult | null {
  const origFull = original.split('\n');
  const origC = origFull.filter(l => l.trim());
  let modC = modified.split('\n').filter(l => l.trim());
  if (origC.length === 0 || modC.length === 0 || origC.length === modC.length) return null;

  const summary: string[] = [];
  const quoteLine = (line: string, max = 50) => {
    const t = line.trim();
    return t.length > max ? `${t.slice(0, max - 3)}...` : t;
  };
  const wordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

  const deduped = detectWholeWallRepetition(modC);
  if (deduped && deduped.length < modC.length) {
    modC = deduped;
    summary.push('The right side looked pasted twice, so we kept just the first copy.');
  }

  // Split-line repair: when the right side has extra lines, the surplus is
  // often ONE sung line the customer's paste broke in two. A single extra
  // line inside a rewritten section is only noticed by the alignment walk at
  // the next verbatim line, which then merges the WRONG line — so glue the
  // most fragment-like candidates (shortest first) into the line above
  // before walking. The merged line must stay within the word cap.
  if (modC.length > origC.length) {
    const isFragment = (line: string): boolean => {
      const t = line.trim();
      const firstAlpha = t.match(/[a-zA-Z]/);
      if (!firstAlpha || firstAlpha[0] !== firstAlpha[0].toLowerCase()) return false;
      return t.split(/\s+/).filter(Boolean).length <= SPLIT_FRAGMENT_MAX_WORDS;
    };
    const endsOpen = (line: string): boolean => !/[.!?,;:]$/.test(line.trim());

    const joined: string[] = [];
    while (modC.length > origC.length) {
      let bestIdx = -1;
      let bestWords = Infinity;
      for (let i = 1; i < modC.length; i++) {
        if (!isFragment(modC[i])) continue;
        if (!endsOpen(modC[i - 1])) continue;
        const mergedWords = wordCount(modC[i - 1]) + wordCount(modC[i]);
        if (mergedWords > MAX_WORDS_PER_LINE) continue;
        const w = wordCount(modC[i]);
        if (w < bestWords) {
          bestWords = w;
          bestIdx = i;
        }
      }
      if (bestIdx < 0) break;
      joined.push(modC[bestIdx].trim());
      modC[bestIdx - 1] = `${modC[bestIdx - 1]} ${modC[bestIdx]}`;
      modC.splice(bestIdx, 1);
    }
    if (joined.length > 0) {
      summary.push(`Joined ${joined.length} line${joined.length === 1 ? '' : 's'} that looked accidentally split in two (starting "${quoteLine(joined[0])}").`);
    }
  }

  const events = alignmentEvents(origC, modC);
  // Orig-indexed output lines; null = no right-side line here, filled from
  // the original during the rebuild.
  const outLines: (string | null)[] = new Array(origC.length).fill(null);
  let lastKept: { origIdx: number; text: string } | null = null;
  let added = 0;
  let duplicatesRemoved = 0;
  const merged: string[] = [];
  const dropped: string[] = [];

  for (const ev of events) {
    if (ev.kind === 'pair') {
      outLines[ev.origIdx] = modC[ev.modIdx];
      lastKept = { origIdx: ev.origIdx, text: modC[ev.modIdx] };
    } else if (ev.kind === 'missing') {
      added++;
    } else {
      const extraText = modC[ev.modIdx];
      if (lastKept && normalizeForDiff(lastKept.text) === normalizeForDiff(extraText)) {
        duplicatesRemoved++;
      } else if (
        lastKept &&
        wordCount(lastKept.text) + wordCount(extraText) <= MAX_WORDS_PER_LINE
      ) {
        outLines[lastKept.origIdx] = `${lastKept.text} ${extraText}`;
        lastKept = { origIdx: lastKept.origIdx, text: outLines[lastKept.origIdx] as string };
        merged.push(quoteLine(extraText));
      } else {
        dropped.push(extraText);
      }
    }
  }

  // Rebuild on the original's skeleton so stanza breaks mirror the song.
  const fixedLines: string[] = [];
  let ci = 0;
  for (const line of origFull) {
    if (!line.trim()) {
      fixedLines.push('');
      continue;
    }
    const v = outLines[ci];
    fixedLines.push(v !== null ? v : origC[ci]);
    ci++;
  }
  while (fixedLines.length && !fixedLines[fixedLines.length - 1].trim()) fixedLines.pop();
  const fixed = fixedLines.join('\n');

  // Defensive: never hand back something the gate would still reject.
  const fixedCount = fixed.split('\n').filter(l => l.trim()).length;
  if (fixedCount !== origC.length) return null;

  if (added > 0) {
    summary.push(
      `Added ${added} line${added === 1 ? '' : 's'} from the original that the right side was missing. Unchanged lines are not billed.`,
    );
  }
  if (duplicatesRemoved > 0) {
    summary.push(`Removed ${duplicatesRemoved} duplicated line${duplicatesRemoved === 1 ? '' : 's'} from the right side.`);
  }
  if (merged.length > 0) {
    summary.push(`Merged ${merged.length} extra line${merged.length === 1 ? '' : 's'} you wrote into the line above (starting "${merged[0]}").`);
  }
  if (dropped.length > 0) {
    summary.push(`Removed ${dropped.length} extra line${dropped.length === 1 ? '' : 's'} that had no matching line in the original (starting "${quoteLine(dropped[0])}"). Cancel and edit by hand if you want to keep ${dropped.length === 1 ? 'it' : 'them'}.`);
  }

  return { fixed, summary, changed: fixed !== modified };
}

/**
 * Normalize whitespace in lyrics text.
 */
export function formatLyrics(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+$/gm, '') // trim trailing spaces per line
    .trim();
}

/**
 * Basic email validation.
 */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * True if the string contains anything that looks like an email address.
 * Used to keep "Your Name" / display-name fields from leaking PII into UIs
 * that render them publicly (e.g. /service/reviews).
 */
export function containsEmail(text: string): boolean {
  if (!text) return false;
  return /[\w.+-]+@[\w-]+\.[\w.-]+/.test(text);
}

/**
 * Check if original and modified lyrics have any differences.
 */
export function areLyricsDifferent(original: string, modified: string): boolean {
  return countChangedSegments(original, modified) > 0;
}

/**
 * True if the URL points at a fully AI-generated song platform (Suno, Udio).
 * The /service order form hard-blocks these links: those vocals are
 * synthesized rather than recorded, so they cannot be cleanly separated and
 * the lyric swap would be unreliable. Tolerates bare-domain pastes
 * ("suno.com/song/x"); the hostname check avoids false positives like
 * "studio.com". Returns false for anything unparseable (partial typing).
 */
export function isAiSongPlatformUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  try {
    const withScheme = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed.replace(/^\/+/, '')}`;
    const host = new URL(withScheme).hostname.toLowerCase();
    return (
      host === 'suno.com' || host.endsWith('.suno.com') ||
      host === 'udio.com' || host.endsWith('.udio.com')
    );
  } catch {
    return false;
  }
}

/**
 * Normalize a US phone number to E.164 (+1XXXXXXXXXX) for SMS sending.
 * Returns null if the input isn't a plausible US number, so callers can
 * silently fall back to email-only instead of storing a bad number.
 */
export function normalizeUsPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}
